import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Button, Typography } from "@/components/atoms";

export interface FileInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label?: React.ReactNode;
  buttonProps?: Omit<React.ComponentProps<typeof Button>, "onClick" | "children" | "type">;
}

export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(
  (
    {
      onChange,
      accept = "image/*,.pdf",
      className,
      label = "Choose File",
      buttonProps,
      disabled,
      multiple,
      ...props
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [displayText, setDisplayText] = useState<string>("No file chosen");

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const openDialog = () => {
      if (!disabled) {
        inputRef.current?.click();
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        if (multiple && files.length > 1) {
          const names = Array.from(files).slice(0, 2).map((f) => f.name).join(", ");
          const extra = files.length > 2 ? `, +${files.length - 2} more` : "";
          setDisplayText(names + extra);
        } else {
          setDisplayText(files[0].name);
        }
      } else {
        setDisplayText("No file chosen");
      }
      onChange(e);
    };

    const hasFile = displayText !== "No file chosen";

    return (
      <div className={className}>
        <input
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleChange}
          disabled={disabled}
          ref={inputRef}
          {...props}
          className="sr-only"
        />

        <div className="flex items-center gap-3">
          <Button
            className="!px-3"
            type="button"
            size="md"
            startIcon="DocumentUpload"
            variant="secondary"
            onClick={openDialog}
            disabled={disabled}
            {...buttonProps}
          >
            {label}
          </Button>

          <Typography
            variant="action"
            className={`${hasFile ? "text-info" : "text-neutral-silver-gray"} truncate max-w-[260px]`}
            aria-live="polite"
          >
            {displayText}
          </Typography>
        </div>
      </div>
    );
  }
);

FileInput.displayName = "FileInput";